import { AiFeature } from '../../../../../models/AiFeature'
import React, { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { TrackEvent, useTracking } from '../../common/tracking'
import { selectRoomSession, selectRoomSessionHasTranscription } from '../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import {
  selectIsExecutionLoading,
  setPollingManuallyExecutedAiFeatureId,
  startExecutedAiFeaturesPolling,
} from '../../../../../store/meetingSummary/executedAiFeatures'
import { useExecuteAiFeatures } from '../hooks/api'
import { getRoomAndAIFeatureTrackingDetails } from '../../tracking'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'
import classnames from 'classnames'
import { Col, Container, Row } from 'react-bootstrap'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'

export interface MeetingScorecardManualRunProps {
  aiFeature: AiFeature
  onManualRun: () => void
}

export const MeetingScorecardManualRun: React.FC<MeetingScorecardManualRunProps> = ({ aiFeature, onManualRun }) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const isLoadingExecutedAiFeatures = useAppSelector(selectIsExecutionLoading)

  const executeAiFeatures = useExecuteAiFeatures()

  const [loading, setLoading] = useState<boolean>(false)

  if (!selectedMeetingTemplate) {
    return null
  }

  const { id: aiFeatureId } = aiFeature

  const _handleRun = () => {
    setLoading(true)
    executeAiFeatures([aiFeatureId], selectedMeetingTemplate.id)
    dispatch(setPollingManuallyExecutedAiFeatureId(aiFeatureId))
    dispatch(startExecutedAiFeaturesPolling())
    onManualRun()
  }

  const handleRun = tracked(_handleRun, {
    eventName: TrackEvent.AI_FEATURE_MANUAL_RUN,
    eventDetails: getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
  })

  return (
    <Row className='d-flex flex-column align-items-center text-center w-100'>
      <Container className='d-flex justify-content-center align-items-center'>
        <Row className='w-50'>
          <Col className='text-center justify-content-center pt-5'>
            <div className='circle-outer'>
              <div className='circle-middle'>
                <div className='circle-inner'>
                  <h1 className='bold zocks-text-gray-500'>?</h1>
                </div>
              </div>
            </div>
            <h6 className='pt-4'>Meeting Score unavailable</h6>
            <div>Meeting Score is currently unavailable for this meeting type. Please run the scoring manually.</div>
            <div className='d-flex justify-content-center py-4'>
              <ScopedBlock
                scope={{
                  scope: 'aifeature:execute',
                  superScope: ['aifeature:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Tooltip title={!hasTranscription ? 'Meeting has no transcription' : undefined}>
                  <Button variant='primary' className='py-2 px-3' onClick={handleRun} disabled={isLoadingExecutedAiFeatures || loading || !hasTranscription}>
                    <i
                      className={classnames('bi bi-arrow-repeat', {
                        'spin-faster': loading,
                      })}
                    ></i>
                    Score this Meeting
                  </Button>
                </Tooltip>
              </ScopedBlock>
            </div>
          </Col>
        </Row>
      </Container>
    </Row>
  )
}
